import type { Mark, Node as PMNode } from "prosemirror-model";
import {ipcInvoke} from "@/common/ipc";
import {KEY_FILE_DIR} from "@/Const";
import {getFileNameExt} from "@/utils";

export type MarkdownImageInfo = { src: string; filename: string; alt: string };

/**
 * Markdown 序列化状态类
 * 用于跟踪序列化过程中的状态
 */
export class MarkdownSerializerState {
  output: string = "";
  closed: boolean = false;
  inTableCell: boolean = false; // 标记是否在表格单元格内
  listDepth: number = 0; // 列表嵌套深度
  images: MarkdownImageInfo[] = []; // 收集的图片信息
  imageCounter: number = 0; // 图片计数器


  /**
   * 确保当前输出与下一个“块级结构”之间至少有一个空行（即 \n\n 分隔）
   * 用于列表/表格等块，避免被解析器粘连成同一段落或拆成多段。
   */
  ensureBlankLineBeforeBlock() {
    if (this.output.length === 0) return;
    if (this.output.endsWith("\n\n")) return;
    if (this.output.endsWith("\n")) {
      this.output += "\n";
    } else {
      this.output += "\n\n";
    }
  }

  /**
   * 确保“块级结构”结束后至少留一个空行（即 \n\n）
   */
  ensureBlankLineAfterBlock() {
    if (this.output.endsWith("\n\n")) return;
    if (this.output.endsWith("\n")) {
      this.output += "\n";
    } else {
      this.output += "\n\n";
    }
  }

  // 转义 Markdown 特殊字符
  esc(str: string, startOfLine: boolean = false): string {
    str = str.replace(/[`*\\~\[\]]/g, "\\$&");
    if (startOfLine) {
      str = str.replace(/^[#\-*+>]/, "\\$&");
    }
    return str;
  }

  // 输出文本
  text(text: string, escape: boolean = true) {
    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const startOfLine = this.output.length === 0 || this.output.endsWith("\n");
      this.output += escape ? this.esc(lines[i], startOfLine) : lines[i];
      if (i < lines.length - 1) {
        this.output += "\n";
      }
    }
  }

  // 输出固定字符串
  write(content: string = "") {
    if (content) {
      this.output += content;
    }
  }

  // 确保以换行结束
  ensureNewLine() {
    if (!this.output.endsWith("\n")) {
      this.output += "\n";
    }
  }

  // 关闭块级元素
  closeBlock(node?: PMNode) {
    this.closed = true;
    this.ensureNewLine();
  }

  // 渲染节点内容
  renderContent(parent: PMNode) {
    parent.forEach((node) => {
      this.render(node);
    });
  }

  // 渲染行内内容
  renderInline(parent: PMNode) {
    parent.forEach((node) => this.render(node));
  }

  // 渲染列表项内容
  renderListItem(listItem: PMNode) {
    let hasNestedList = false;

    // 先处理列表项的内容
    listItem.forEach((child) => {
      const nodeName = child.type.name;
      if (["orderedList", "bulletList", "taskList"].includes(nodeName)) {
        hasNestedList = true;
      } else if (nodeName === "paragraph") {
        // 渲染段落内容但不添加换行
        this.renderInline(child);
      } else {
        this.render(child);
      }
    });

    this.ensureNewLine();

    // 如果有嵌套列表,渲染它们
    if (hasNestedList) {
      listItem.forEach((child) => {
        const nodeName = child.type.name;
        if (["orderedList", "bulletList", "taskList"].includes(nodeName)) {
          this.render(child);
        }
      });
    }
  }

  // 重复字符串
  repeat(str: string, count: number): string {
    return str.repeat(count);
  }

  // 包裹块级内容
  wrapBlock(
    delim: string,
    firstDelim: string | null,
    node: PMNode,
    callback: () => void
  ) {
    const old = this.output;
    this.output = "";
    callback();
    let content = this.output;
    this.output = old;

    // 如果是 blockquote 且内容末尾是 \n,则去掉末尾的 \n
    if (node.type.name === "blockquote" && content.endsWith("\n")) {
      content = content.slice(0, -1);
    }

    const lines = content.split("\n");
    for (let i = 0; i < lines.length; i++) {
      this.output += (i === 0 && firstDelim !== null ? firstDelim : delim) + lines[i];
      if (i < lines.length - 1) {
        this.output += "\n";
      }
    }
    this.closeBlock();
    // blockquote 后面需要额外空行，避免后续内容被错误解析为引用
    if (node.type.name === "blockquote") {
      this.output += "\n";
    }
  }

  // 渲染节点
  render(node: PMNode) {
    // 处理不同类型的节点
    const nodeName = node.type.name;

    // 先处理标记（marks）
    const marks = (node as any).marks || [];
    let openMarks = "";
    let closeMarks = "";

    // 检查是否有下划线与其他样式混合（链接不算，下划线+链接时保留下划线）
    const hasUnderline = marks.some((m: any) => m.type.name === "underline");
    const hasOtherStyles = marks.some(
      (m: any) =>
        m.type.name !== "underline" &&
        ["strong", "bold", "em", "italic", "strike", "strikethrough", "code"].includes(
          m.type.name
        )
    );
    const shouldSkipUnderline = hasUnderline && hasOtherStyles;

    for (const mark of marks) {
      // 如果下划线与其他样式混合，跳过下划线的输出
      if (shouldSkipUnderline && mark.type.name === "underline") {
        continue;
      }
      const markResult = this.serializeMark(mark);
      if (markResult) {
        openMarks += markResult.open;
        closeMarks = markResult.close + closeMarks;
      }
    }

    // 输出标记开始
    if (openMarks) {
      this.write(openMarks);
    }

    // 处理节点本身
    switch (nodeName) {
      case "text":
        this.text((node as any).text || "", false);
        break;

      case "paragraph":
        this.renderInline(node);
        // 在表格单元格内不添加块级换行
        if (!this.inTableCell) {
          // 无论段落是否为空，都需要添加换行以保持空行
          // 空段落在 Markdown 中应表现为一个空行
          this.output += "\n";
        }
        break;

      case "heading": {
        const level = (node as any).attrs?.level || 1;
        this.write(this.repeat("#", level) + " ");
        this.renderInline(node);
        this.closeBlock();
        break;
      }

      case "blockquote":
        this.wrapBlock("> ", null, node, () => this.renderContent(node));
        break;

      case "codeBlock": {
        const params = (node as any).attrs?.language || "";
        this.write("```" + params + "\n");
        this.text(node.textContent, false);
        this.ensureNewLine();
        this.write("```");
        this.closeBlock();
        break;
      }

      case "horizontalRule":
        this.ensureNewLine();
        this.write("\n---");
        this.closeBlock();
        break;

      case "hardBreak":
        this.write("  \n");
        break;

      case "orderedList": {
        const isTopLevelList = this.listDepth === 0;
        if (isTopLevelList && !this.inTableCell) {
          this.ensureBlankLineBeforeBlock();
        }
        this.listDepth++;
        let orderNum = (node as any).attrs?.start || 1;
        node.forEach((child) => {
          // 添加缩进 (每层4个空格,符合 CommonMark 标准)
          const indent = "    ".repeat(this.listDepth - 1);
          this.write(`${indent}${orderNum}. `);
          this.renderListItem(child);
          orderNum++;
        });
        this.listDepth--;
        if (this.listDepth === 0) {
          this.closeBlock();
          if (!this.inTableCell) {
            this.ensureBlankLineAfterBlock();
          }
        }
        break;
      }

      case "bulletList":
        if (this.listDepth === 0 && !this.inTableCell) {
          this.ensureBlankLineBeforeBlock();
        }
        this.listDepth++;
        node.forEach((child) => {
          // 添加缩进 (每层4个空格,符合 CommonMark 标准)
          const indent = "    ".repeat(this.listDepth - 1);
          this.write(`${indent}- `);
          this.renderListItem(child);
        });
        this.listDepth--;
        if (this.listDepth === 0) {
          this.closeBlock();
          if (!this.inTableCell) {
            this.ensureBlankLineAfterBlock();
          }
        }
        break;

      case "listItem":
        // list_item 由父列表处理,这里不需要额外处理
        this.renderContent(node);
        break;

      case "taskList":
        if (this.listDepth === 0 && !this.inTableCell) {
          this.ensureBlankLineBeforeBlock();
        }
        this.listDepth++;
        node.forEach((child) => {
          // 添加缩进 (每层4个空格,符合 CommonMark 标准)
          const indent = "    ".repeat(this.listDepth - 1);
          const checked = (child as any).attrs?.checked;
          this.write(`${indent}${checked ? "- [x] " : "- [ ] "}`);
          this.renderListItem(child);
        });
        this.listDepth--;
        if (this.listDepth === 0) {
          this.closeBlock();
          if (!this.inTableCell) {
            this.ensureBlankLineAfterBlock();
          }
        }
        break;

      case "taskItem":
        // task_item 由父 task_list 处理,这里不需要额外处理
        this.renderContent(node);
        break;

      case "image": {
        const src = (node as any).attrs?.src || "";
        const alt = (node as any).attrs?.alt || "";
        const title = (node as any).attrs?.title;
        // 如果图片是网络地址或 atom,收集它并使用相对路径
        if (
          src &&
          (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("atom://"))
        ) {
          // 获取文件扩展名
          let ext = "png";
          if (src.startsWith("data:image/")) {
            const match = src.match(/^data:image\/(\w+);/);
            if (match) ext = match[1];
          } else {
            const urlMatch = src.match(/\.(\w+)(?:\?|$)/);
            if (urlMatch) ext = urlMatch[1];
          }

          // 使用图片原始 ID 作为文件名
          let filename: string;
          if (src.startsWith("atom://")) {
            // 从 atom:// URL 中提取图片 ID
            const imageId = src.replace("atom://", "");
            filename = `${imageId}`;
          } else {
            // 网络图片使用计数器
            this.imageCounter++;
            filename = `image_${this.imageCounter}.${ext}`;
          }

          this.images.push({ src, filename, alt });
          // 使用相对路径
          this.write(`![${alt}](asset/${filename}${title ? ' "' + title + '"' : ""})`);
        } else {
          // 已经是相对路径或其他格式,直接使用
          this.write(`![${alt}](${src}${title ? ' "' + title + '"' : ""})`);
        }
        // 图片作为块级元素,后面需要换行
        this.closeBlock();
        break;
      }

      case "table":
        // 表格处理
        // 表格前添加空行，与前面内容分隔
        this.ensureNewLine();
        this.output += "\n";

        let isFirstRow = true;
        node.forEach((row) => {
          // 渲染每一行
          this.render(row);
          // 在第一行后添加分隔线
          if (isFirstRow) {
            this.write("|");
            row.forEach(() => this.write(" --- |"));
            this.ensureNewLine();
            isFirstRow = false;
          }
        });
        this.closeBlock();
        // 表格后添加空行，与后面内容分隔
        this.output += "\n";
        break;

      case "tableRow":
        this.write("|");
        node.forEach((cell) => {
          this.write(" ");
          // 使用 render 而不是 renderInline,这样会触发 table_cell 的处理
          this.render(cell);
          this.write(" |");
        });
        this.ensureNewLine();
        break;

      case "tableCell":
      case "tableHeader":
        // 设置标志,表示进入表格单元格
        this.inTableCell = true;
        this.renderInline(node);
        // 清除标志,表示离开表格单元格
        this.inTableCell = false;
        break;

      // 自定义节点：LaTeX 行内公式
      case "latexInline": {
        const latexInline = (node as any).attrs?.metaData || node.textContent || "";
        this.write(`$${String(latexInline).trim()}$`);
        break;
      }

      // 自定义节点：LaTeX 块级公式
      case "latexBlock": {
        const latexBlock = (node as any).attrs?.metaData || node.textContent || "";
        this.write("$$");
        this.ensureNewLine();
        this.text(String(latexBlock), false);
        this.ensureNewLine();
        this.write("$$");
        this.closeBlock();
        break;
      }

      // 自定义节点：深度链接
      case "deeplinkNode": {
        const deeplinkId = (node as any).attrs?.deeplinkId || "";
        const deeplinkText = (node as any).attrs?.text || node.textContent || "";
        // 使用特殊格式保存 deeplink，可以根据需要调整
        this.write(`[${deeplinkText}](deeplink://${deeplinkId})`);
        break;
      }

      case "youtube": {
        const youtubeEmbedUrl = (node as any).attrs?.src || "";
        // 从 embed URL 还原为标准 watch URL
        // embed 格式: https://www.youtube.com/embed/VIDEO_ID 或 https://www.youtube-nocookie.com/embed/VIDEO_ID
        let youtubeWatchUrl = youtubeEmbedUrl;
        const embedMatch = youtubeEmbedUrl.match(
          /(?:youtube\.com|youtube-nocookie\.com)\/embed\/([-\w]+)/
        );
        if (embedMatch && embedMatch[1]) {
          const videoId = embedMatch[1];
          youtubeWatchUrl = `https://www.youtube.com/watch?v=${videoId}`;
        }
        this.write(`${youtubeWatchUrl}`);
        this.closeBlock();
        break;
      }

      // 默认处理：渲染子节点
      default:
        if ((node as any).content && (node as any).content.size > 0) {
          this.renderContent(node);
        }
        break;
    }

    // 输出标记结束
    if (closeMarks) {
      this.write(closeMarks);
    }
  }

  // 序列化标记
  serializeMark(mark: Mark | any): { open: string; close: string } | null {
    switch (mark.type.name) {
      case "strong":
      case "bold":
        return { open: "**", close: "**" };

      case "em":
      case "italic":
        return { open: "*", close: "*" };

      case "code":
        return { open: "`", close: "`" };

      case "underline":
        // Markdown 原生不支持下划线，使用 HTML
        return { open: "<u>", close: "</u>" };

      case "strike":
      case "strikethrough":
        return { open: "~~", close: "~~" };

      case "link": {
        const href = (mark as any).attrs?.href || "";
        const title = (mark as any).attrs?.title;
        return {
          open: "[",
          close: `](${href}${title ? ' "' + title + '"' : ""})`,
        };
      }

      case "highlight": {
        // Markdown 原生不支持高亮，使用 HTML
        const bgColor = (mark as any).attrs?.color || "yellow";
        return {
          open: `<mark style="background-color: ${bgColor}">`,
          close: "</mark>",
        };
      }

      case "textStyle":
        // 不导出 textStyle 样式（避免输出隐藏的 span 元素）
        return null;

      default:
        return null;
    }
  }
}

export function serializeProseMirrorToMarkdown(doc: PMNode): {
  markdown: string;
  images: MarkdownImageInfo[];
} {
  const state = new MarkdownSerializerState();
  state.renderContent(doc);
  return {
    markdown: state.output.trim(),
    images: state.images,
  };
}

/**
 * 下载图片并转换为 Blob
 * @param src 图片地址 (支持网络地址 http/https 和本地 atom 图片)
 * @returns Blob 对象
 */
export const downloadImageAsBlob = async (src: string): Promise<Blob | null> => {
  try {
    if (src.startsWith("http://") || src.startsWith("https://")) {
      // 网络图片
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Failed to fetch image: ${src}`);
      return await response.blob();
    } else if (src.startsWith("atom://")) {
      // atom 本地图片,通过 IPC 读取
      const imageId = src.replace("atom://", "");
      const filePath = localStorage.getItem(KEY_FILE_DIR)+imageId;
      let base64Data = `data:image/${getFileNameExt(imageId)};base64,` + await ipcInvoke('read-file-base64',{filePath:filePath});

      if (!base64Data) {
        console.error("Failed to read atom image:", imageId);
        return null;
      }

      // 将 base64 转换为 Blob
      const base64Content = base64Data.split(",")[1];
      const mimeMatch = base64Data.match(/^data:([^;]+);/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    }
    return null;
  } catch (error) {
    console.error("Error downloading image:", src, error);
    return null;
  }
};



